import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Stack,
  Typography,
} from '@mui/material';
import PersonRemoveOutlinedIcon from '@mui/icons-material/PersonRemoveOutlined';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import { useTranslation } from 'react-i18next';
import PresenceBadge from './PresenceBadge';
import { getInitials } from './presence';

const CompanionCard = ({
  companion,
  onChat,
  onInvite,
  onOpenProfile,
  onRemove,
  onView,
  variant = 'card',
}) => {
  const { t } = useTranslation();
  const openProfile = () => onOpenProfile?.(companion);

  if (variant === 'compact') {
    return (
      <Stack
        component={onOpenProfile ? 'button' : 'div'}
        type={onOpenProfile ? 'button' : undefined}
        alignItems="center"
        onClick={openProfile}
        sx={{
          p: 0,
          border: 0,
          bgcolor: 'transparent',
          color: 'inherit',
          cursor: onOpenProfile ? 'pointer' : 'default',
          font: 'inherit',
          minWidth: 0,
          width: '100%',
          maxWidth: 112,
          mx: 'auto',
          textAlign: 'center',
          '&:focus-visible': {
            outline: '3px solid',
            outlineColor: 'secondary.light',
            outlineOffset: 4,
            borderRadius: 2,
          },
        }}
      >
        <Avatar
          src={companion.avatarUrl || undefined}
          alt={companion.displayName}
          sx={{ width: 56, height: 56 }}
        >
          {getInitials(companion.displayName)}
        </Avatar>
        <Typography
          variant="h4"
          sx={{ mt: 1, maxWidth: '100%' }}
          noWrap
        >
          {companion.displayName}
        </Typography>
        <PresenceBadge presence={companion.presence} size="small" noWrap />
      </Stack>
    );
  }

  return (
    <Card
      variant="outlined"
      sx={{
        transition: 'border-color 160ms ease',
      }}
    >
      <CardContent>
        <Stack spacing={2}>
          <Stack
            component={onOpenProfile ? 'button' : 'div'}
            type={onOpenProfile ? 'button' : undefined}
            direction="row"
            spacing={2}
            alignItems="center"
            onClick={openProfile}
            onKeyDown={(event) => {
              if (!onOpenProfile) return;
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                openProfile();
              }
            }}
            sx={{
              width: '100%',
              minWidth: 0,
              p: 0,
              border: 0,
              bgcolor: 'transparent',
              color: 'inherit',
              cursor: onOpenProfile ? 'pointer' : 'default',
              font: 'inherit',
              textAlign: 'left',
              borderRadius: 2,
              transition: 'background-color 160ms ease, transform 160ms ease',
              '&:hover': onOpenProfile
                ? {
                    bgcolor: 'rgba(15, 76, 69, 0.055)',
                    transform: 'translateY(-1px)',
                  }
                : undefined,
              '&:focus-visible': {
                outline: '3px solid',
                outlineColor: 'secondary.light',
                outlineOffset: 4,
              },
            }}
          >
            <Box sx={{ position: 'relative' }}>
              <Avatar
                src={companion.avatarUrl || undefined}
                alt={companion.displayName}
                sx={{ width: 58, height: 58 }}
              >
                {getInitials(companion.displayName)}
              </Avatar>
              <Box
                sx={{
                  position: 'absolute',
                  right: 0,
                  bottom: 2,
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  border: '2px solid',
                  borderColor: 'background.paper',
                  bgcolor:
                    companion.presence?.isOnline
                      ? 'success.main'
                      : companion.presence?.lastSeenAt
                        ? 'secondary.main'
                        : 'text.disabled',
                }}
              />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="h3" noWrap>
                {companion.displayName}
              </Typography>
              <PresenceBadge presence={companion.presence} />
              <Typography sx={{ mt: 1 }} fontWeight={700}>
                {t('companions.card.gamesOnShelf', { count: companion.gamesKept })}
              </Typography>
            </Box>
          </Stack>
          <Box
            sx={{ borderTop: '1px solid', borderColor: 'divider', pt: 2 }}
            onClick={(event) => event.stopPropagation()}
          >
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              {onView ? (
                <Button
                  fullWidth
                  variant="contained"
                  color="secondary"
                  onClick={(event) => {
                    event.stopPropagation();
                    onView(companion);
                  }}
                >
                  {t('companions.card.viewShelf')}
                </Button>
              ) : null}
              <Button
                fullWidth
                variant="outlined"
                onClick={(event) => {
                  event.stopPropagation();
                  onInvite?.(companion);
                }}
              >
                {t('companions.card.inviteToNight')}
              </Button>
              {onChat ? (
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<ChatBubbleOutlineIcon />}
                  onClick={(event) => {
                    event.stopPropagation();
                    onChat(companion);
                  }}
                >
                  {t('chat.actions.message')}
                </Button>
              ) : null}
              {onRemove ? (
                <Button
                  fullWidth
                  variant="text"
                  color="inherit"
                  startIcon={<PersonRemoveOutlinedIcon />}
                  onClick={(event) => {
                    event.stopPropagation();
                    onRemove(companion);
                  }}
                  sx={{
                    color: 'text.secondary',
                    '&:hover': {
                      bgcolor: 'rgba(168, 58, 50, 0.08)',
                      color: 'error.main',
                    },
                  }}
                >
                  {t('companions.card.remove')}
                </Button>
              ) : null}
            </Stack>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default CompanionCard;
